import {Box, Typography} from '@mui/material';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import './NewJobDetailHeader.scss';
import ExitJobModal from '../exit-job-modal/ExitJobModal';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../../redux/store';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import {AI_REVIEW_JOB_TYPE} from '../../../constant/AiReviewConstants';

interface NewJobDetailHeaderProps {
  showExitModal: boolean;
  setShowExitModal: (open: boolean) => void;
  onSaveDraft: () => void;
  onSubmit: () => void;
  onExitWithoutSaving: () => void;
  hasUnsavedChanges: boolean;
  isSaveDraftDisabled?: boolean;
  isSubmitDisabled?: boolean;
  saveDraftLabel: string;
}

const NewJobDetailHeader = ({
  showExitModal,
  setShowExitModal,
  onSaveDraft,
  onSubmit,
  onExitWithoutSaving,
  hasUnsavedChanges,
  isSaveDraftDisabled = false,
  isSubmitDisabled = false,
  saveDraftLabel,
}: NewJobDetailHeaderProps) => {
  const handleExitJobCreation = () => {
    if (hasUnsavedChanges) {
      setShowExitModal(true);
      return;
    }

    onExitWithoutSaving();
  };

  const cloneJobName = useSelector(
    (state: RootState) => state.aiReviewV2.cloneJobName
  );
  const jobType = useSelector((state: RootState) => state.aiReviewV2.jobType);
  const formData = useSelector((state: RootState) => state.aiReviewV2.formData);

  const handleCloseModal = () => {
    setShowExitModal(false);
  };

  const headerTitle =
    jobType === AI_REVIEW_JOB_TYPE.issueCoding
      ? t('ai.review.newJobDetail.newIssueCodingJobHeader')
      : t('ai.review.newJobDetail.newRelevancyJobHeader');
  const isEditJob = Boolean(formData.jobId);
  const editJobName = formData.jobDetail.jobName;
  const parentReviewJobID = formData.parentReviewJobID;

  return (
    <Box className="new-job-detail-header">
      <Box className="new-job-detail-header-exit-button">
        <ArrButton
          variant="text"
          size="small"
          aria-label={t('ai.review.newJobDetail.exitLabel')}
          onClick={handleExitJobCreation}
          dataTestId="exit-job-button"
        >
          <NavigateBeforeIcon fontSize="small" className="button-arrow" />
          {t('ai.review.newJobDetail.exitLabel')}
        </ArrButton>

        {isEditJob ? (
          <Typography
            component="h1"
            className={`${
              parentReviewJobID ? 'clone-job-header-title' : 'header-title'
            } typo-text subhead1_size`}
            data-testid="edit-job-new-job-detail-header"
          >
            {editJobName}
            {parentReviewJobID ? (
              <span className="clone-job-suffix">
                {' '}
                {t('ai.review.newJobDetail.cloneHeaderSuffix')}
              </span>
            ) : null}
          </Typography>
        ) : cloneJobName ? (
          <Typography
            component="h1"
            className="clone-job-header-title typo-text subhead1_size"
            data-testid="clone-job-new-job-detail-header"
          >
            {headerTitle}
            <span className="clone-job-suffix">
              {' '}
              {t('ai.review.newJobDetail.cloneHeaderSuffix')}
            </span>
          </Typography>
        ) : (
          <Typography
            component="h1"
            className="header-title typo-text subhead1_size"
            data-testid="new-job-detail-header-header"
          >
            {headerTitle}
          </Typography>
        )}
      </Box>

      <Box className="action-button">
        <ArrButton
          dataTestId="save-draft-btn"
          className="save-draft-btn"
          variant="secondary"
          size="medium"
          onClick={onSaveDraft}
          aria-label={saveDraftLabel}
          aria-disabled={isSaveDraftDisabled}
          isAriaDisabled={isSaveDraftDisabled}
          disabled={isSaveDraftDisabled}
        >
          {saveDraftLabel}
        </ArrButton>
        <ArrButton
          dataTestId="submit-btn"
          className="submit-btn"
          variant="primary"
          size="medium"
          onClick={onSubmit}
          aria-label={t('common.submit')}
          aria-disabled={isSubmitDisabled}
          isAriaDisabled={isSubmitDisabled}
          disabled={isSubmitDisabled}
        >
          {t('common.submit')}
        </ArrButton>
      </Box>

      <ExitJobModal open={showExitModal} onClose={handleCloseModal} />
    </Box>
  );
};

export default NewJobDetailHeader;
